/**
 * Admin loop human-wait RPC definitions (the human issue).
 *
 * The operator's loop-wait inbox: every open `loop_human_waits` row of a named
 * customer, and the one write that answers one. Same operator-surface rules as
 * `orcaloops-human-waits.rpc.ts` / `orcaloops-outcomes.rpc.ts` — behind
 * `AdminMiddleware`, with an explicit `customerId` that is named, never inferred.
 * The member-facing, actor-scoped variant is a later group.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { LoopHumanWait } from "@fm/db/models/LoopHumanWait";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

/**
 * The customer whose loop-wait inbox is being read.
 *
 * @since 1.0.0
 */
export const LoopHumanWaitInboxInput = Schema.Struct({
  customerId: Schema.String,
});
export type LoopHumanWaitInboxInput = Schema.Schema.Type<typeof LoopHumanWaitInboxInput>;

/**
 * One open wait, as the inbox renders it — `LoopHumanWait.json` with
 * `deferredToken` omitted, because the `@effect/workflow` DurableDeferred token
 * that resolves the execution must never leave the api (the `OutcomeInboxRow`
 * rule).
 *
 * @since 1.0.0
 */
export const LoopHumanWaitInboxRow = LoopHumanWait.json.pipe(Schema.omit("deferredToken"));
export type LoopHumanWaitInboxRow = Schema.Schema.Type<typeof LoopHumanWaitInboxRow>;

/**
 * Answer one open wait.
 *
 * The payload names the wait by id and carries the operator's reply text and
 * chosen option — never a capability token: an operator holds no `deferredToken`
 * (the inbox omits it), and on an AUTHENTICATED surface a customer-scoped read
 * beats an un-scoped token lookup.
 *
 * @since 1.0.0
 */
export const AnswerLoopHumanWaitInput = Schema.Struct({
  customerId: Schema.String,
  waitId: Schema.String,
  text: Schema.String,
  option: Schema.optional(Schema.String),
});
export type AnswerLoopHumanWaitInput = Schema.Schema.Type<typeof AnswerLoopHumanWaitInput>;

/**
 * What the answer wrote: the wait, its executor, and when it was answered.
 *
 * @since 1.0.0
 */
export const AnswerLoopHumanWaitResult = Schema.Struct({
  waitId: Schema.String,
  executorId: Schema.String,
  answeredAt: Schema.DateTimeUtc,
});
export type AnswerLoopHumanWaitResult = Schema.Schema.Type<typeof AnswerLoopHumanWaitResult>;

/**
 * Wire form of the service's `LoopHumanWaitNotAnswerable` — somebody answered
 * the wait first (the CAS lost).
 *
 * Declared here rather than reusing the service error because that one is a
 * `Data.TaggedError` (no schema) and every rpc error must be encodable.
 *
 * @since 1.0.0
 */
export class LoopHumanWaitNotAnswerableError extends Schema.TaggedError<LoopHumanWaitNotAnswerableError>()(
  "LoopHumanWaitNotAnswerableError",
  {
    reason: Schema.String,
  },
) {}

/**
 * Admin loop human-wait RPC group.
 *
 * @since 1.0.0
 */
export class AdminLoopHumanWaitRpcs extends RpcGroup.make(
  Rpc.make("admin.loops.humanWaits.list", {
    payload: LoopHumanWaitInboxInput,
    success: Schema.Array(LoopHumanWaitInboxRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.loops.humanWaits.answer", {
    payload: AnswerLoopHumanWaitInput,
    success: AnswerLoopHumanWaitResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      LoopHumanWaitNotAnswerableError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
) {}
